import React from 'react';

import Tabs, { TabsProps } from 'antd/lib/tabs';
import Modal from 'antd/lib/modal';
import GoogleMapReact from 'google-map-react';

import { LocationPortal } from '@app/objects/CompanyPortal';

import '@app/scss/components/locations.scss';
import { Location } from '@app/objects/Location';
import WithPathLink from '@app/components/UI/WithPathLink/WithPathLink';

interface IPros {
	locations: Array<LocationPortal>;
	containerClassName?: string;
	content?: (location: LocationPortal) => JSX.Element;
	reviews?: (location: LocationPortal) => React.ReactNode;
}

interface MapProps {
	address: string;
}

function codeAddress(map, maps, address: string) {
	const geocoder = new maps.Geocoder();
	geocoder.geocode({ address }, (results, status) => {
		if (status === 'OK') {
			map.setCenter(results[0].geometry.location);
			// eslint-disable-next-line
			new maps.Marker({
				map,
				position: results[0].geometry.location,
			});
		} else {
			console.log(`Geocode was not successful for the following reason: ${status}`);
		}
	});
}

const SimpleMap: React.FC<MapProps> = ({ address }) => {
	const defaultProps = {
		center: { lat: -34.397, lng: 150.644 },
		zoom: 12,
	};

	const renderMarker = (map, maps) => {
		return codeAddress(map, maps, address);
	};

	return (
		<div className="location-map">
			<GoogleMapReact
				bootstrapURLKeys={{ key: 'AIzaSyB_Vis358nPu31ZjN8OUdxbMwr9H9JgSso' }}
				defaultCenter={defaultProps.center}
				defaultZoom={defaultProps.zoom}
				onGoogleApiLoaded={({ map, maps }) => renderMarker(map, maps)}
			/>
		</div>
	);
};

interface ILocationModal {
	location: Location;
	buttonClassName?: string;
	buttonValue?: JSX.Element;
}

export const LocationModal: React.FC<ILocationModal> = ({ location, buttonValue, buttonClassName }) => {
	const [visible, setVisible] = React.useState<boolean>(false);

	const close = () => setVisible(false);

	const open = () => setVisible(true);
	const { addressEn, zip, city } = location;

	return (
		<>
			<button type="button" onClick={open} className={`btn ${buttonClassName || ''}`}>
				{buttonValue || location.nameEn}
			</button>
			<Modal
				open={visible}
				onCancel={close}
				title={<>
					<WithPathLink prefix="clinic" path={location.portalPathEn}>
						{location.nameEn}
					</WithPathLink>
				</>}
				width={600}
				className="location-popap"
				footer={null}
			>
				<div>
					<strong>Address:</strong>
					{' '}
					{addressEn}
					,
					{' '}
					{city}
					,
					{' '}
					{zip}
				</div>
				<div style={{ marginBottom: '10px' }}>
					<strong>Phone:</strong>
					{' '}
					{location.phone}
				</div>
				<SimpleMap address={`${addressEn}${city ? `, ${city}` : ''}${zip ? `, ${zip}` : ''}`} />
			</Modal>
		</>
	);
};

const Locations: React.FC<IPros> = ({
	locations, containerClassName, content, reviews,
}) => {
	if (locations.length === 1) {
		const item = locations[0];
		return (
			<div id="locations" className={`mb20 locations scroll-top ${containerClassName || ''}`}>
				<div className="location-tab">
					<h4>
						{item.nameEn}
						{item.addressEn ? ` - ${item.addressEn}` : ''}
					</h4>
					<div className="row card-panel location">
						<div className="location-inner">
							<SimpleMap address={item.addressEn} />
							<div className="info-block">
								{content && content(item)}
							</div>
						</div>
						{reviews && <div className="location-reviews">
							{reviews && reviews(item)}
						</div>
						}
					</div>
				</div>
			</div>
		);
	}

	const items: TabsProps['items'] = locations.map((item, index) => {
		return {
			key: index.toString(),
			label: `${item.nameEn}${item.addressEn ? ` - ${item.addressEn}` : ''}`,
			className: 'location-tab',
			children: <div className="row card-panel location">
				<div className="location-inner">
					<SimpleMap address={item.addressEn} />
					<div className="info-block">
						{content && content(item)}
					</div>
				</div>
				{reviews && <div className="location-reviews">
					{reviews && reviews(item)}
				</div>
				}
			</div>,
		};
	});

	return (
		locations.length > 0 ? <div id="locations" className={`mb20 locations scroll-top ${containerClassName || ''}`}>
			<Tabs type="card" defaultActiveKey="0" items={items} />
		</div> : null
	);
};

export default Locations;
